import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { PageHeader } from '../../components/layout/PageHeader';
import { useCurrentUser } from '../auth';
import { listAccounts } from '../accounts/api';
import { ApiError } from '../../lib/api';
import { canDeleteBroadcastFromList, deleteConfirmCopy } from '../../lib/broadcastPermissions';
import {
  BROADCAST_STATUS_FILTER_OPTIONS,
  DEFAULT_BROADCAST_SORT,
  filterBroadcastsByStatus,
  nextSort,
  sortBroadcasts,
  type BroadcastSort,
  type BroadcastStatusFilter,
} from '../../lib/broadcastListView';
import type { Broadcast } from '../../types/broadcast';
import { deleteBroadcast, listBroadcasts } from './api';
import { brandLabel } from './audienceDescription';
import { BroadcastTable } from './BroadcastTable';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';

// Stage 6A §5.1 — broadcasts landing + list. v6 adds a permission-gated Delete
// action per row (draft = hard delete; sent = soft delete) and an admin-only
// link into the deleted-broadcast recovery view. v7 adds a status filter +
// sortable columns (default: most-recently-sent first, drafts at the bottom),
// both held in page state so they persist for the session.

export function BroadcastsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const me = useCurrentUser();
  const broadcasts = useQuery({ queryKey: ['broadcasts'], queryFn: listBroadcasts });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  // Carried in via navigation state from the detail page's delete (which
  // redirects here) — surface it once as a toast, then clear the nav state
  // so a refresh/back doesn't re-fire it.
  useEffect(() => {
    const navToast = (location.state as { toast?: string } | null)?.toast;
    if (navToast) {
      toast.success(navToast);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [pendingDelete, setPendingDelete] = useState<Broadcast | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // v7 — status filter + sort, held in page state (persist for the session).
  const [statusFilter, setStatusFilter] = useState<BroadcastStatusFilter>('all');
  const [sort, setSort] = useState<BroadcastSort>(DEFAULT_BROADCAST_SORT);

  const brandName = (id: string) => accounts.data?.find((a) => a.id === id)?.name ?? '—';

  const all = broadcasts.data ?? [];
  const view = useMemo(
    () =>
      sortBroadcasts(filterBroadcastsByStatus(all, statusFilter), sort, (b) =>
        brandLabel(b.audienceScope, brandName),
      ),
    // brandName closes over accounts.data, so depend on it explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, statusFilter, sort, accounts.data],
  );

  const del = useMutation({
    mutationFn: (b: Broadcast) => deleteBroadcast(b.id),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      setPendingDelete(null);
      setDeleteError(null);
      toast.success(res.kind === 'hard' ? 'Draft deleted.' : 'Broadcast deleted.');
    },
    onError: (err) => setDeleteError(err instanceof ApiError ? err.message : 'Delete failed.'),
  });

  const copy = pendingDelete ? deleteConfirmCopy(pendingDelete.status) : null;

  return (
    <div>
      <PageHeader
        title="Broadcasts"
        description="Send email and SMS messages to your contacts."
        actions={
          <>
            {me.role === 'admin' && (
              <Button variant="outline" asChild>
                <Link to="/broadcasts/deleted">Deleted</Link>
              </Button>
            )}
            <Button onClick={() => navigate('/broadcasts/new')}>New broadcast</Button>
          </>
        }
      />


      {broadcasts.isLoading ? (
        <CenteredSpinner />
      ) : broadcasts.error ? (
        <p className="text-sm text-red-600">Couldn't load broadcasts.</p>
      ) : all.length === 0 ? (
        <p className="text-sm text-slate-600">
          No broadcasts yet. Create one to send bulk email / SMS to a Brand's groups and locations.
        </p>
      ) : (
        <>
          <div className="mb-3 flex items-center gap-2">
            <label htmlFor="broadcast-status-filter" className="text-sm text-slate-600">
              Status
            </label>
            <SelectField
              id="broadcast-status-filter"
              size="sm"
              aria-label="Status"
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as BroadcastStatusFilter)}
              options={BROADCAST_STATUS_FILTER_OPTIONS}
            />
          </div>

          {view.length > 0 ? (
            <BroadcastTable
              mode="active"
              broadcasts={view}
              brandName={brandName}
              canDelete={(b) => canDeleteBroadcastFromList(b, me.uid, me.role)}
              onDelete={(b) => {
                setDeleteError(null);
                setPendingDelete(b);
              }}
              sort={sort}
              onSort={(key) => setSort((s) => nextSort(s, key))}
            />
          ) : (
            <p className="text-sm text-slate-600">No broadcasts match this filter.</p>
          )}
        </>
      )}

      {pendingDelete && copy && (
        <ConfirmDeleteModal
          title={copy.title}
          message={copy.message}
          confirmLabel={copy.confirmLabel}
          busy={del.isPending}
          errorText={deleteError}
          onCancel={() => {
            if (!del.isPending) {
              setPendingDelete(null);
              setDeleteError(null);
            }
          }}
          onConfirm={() => del.mutate(pendingDelete)}
        />
      )}
    </div>
  );
}
